import React, { useState, useEffect, useRef } from 'react'
import {
  useSpring,
  animated as a,
  useChain,
  interpolate,
  config,
} from 'react-spring'
import Img from 'gatsby-image'
import Shuffle from 'shufflejs'

// Project images, along with description
import useImageQuery from '../../graphql/projectImagesQuery'

// Components
const Projects = ({ isVisible }) => {
  // Load/View detection
  const [loaded, setLoaded] = useState(false)

  const load = isVisible => {
    if (isVisible && !loaded) {
      setLoaded(true)
    }
  }

  useEffect(() => {
    load(isVisible)
  }, [isVisible])

  // Load images from graphql query
  const [images, setImages] = useState(useImageQuery)

  // Create a filtering container
  const [shuffle, setShuffle] = useState(null)

  // References to DOM elements
  let containerRef = useRef(null)
  let jsFilter = useRef(null)
  let reactFilter = useRef(null)
  let allFilter = useRef(null)
  let nodeFilter = useRef(null)

  // Renders all project images
  const renderAll = items => {
    return items.map(item => (
      <Image
        data={item.type}
        key={item.id}
        imgSrc={item.image}
        desc={item.desc}
      />
    ))
  }

  // Run once to initialize shuffler
  useEffect(() => {
    if (loaded) {
      setShuffle(
        new Shuffle(containerRef, {
          itemSelector: '.image',
          isCentered: true,
          gutterWidth: 50,
        })
      )
    }
  }, [loaded])

  // When shuffler is initialized, assign click handlers
  useEffect(() => {
    if (shuffle) {
      jsFilter.addEventListener('click', () => {
        shuffle.filter('js')
      })
      reactFilter.addEventListener('click', () => {
        shuffle.filter('react')
      })
      nodeFilter.addEventListener('click', () => {
        shuffle.filter('node')
      })
      allFilter.addEventListener('click', () => {
        shuffle.filter()
      })
    }
  }, [shuffle])

  // Animations
  let growRef = useRef()
  let opacityRef = useRef()

  const { scale, width, rotate } = useSpring({
    from: {
      scale: 0,
      width: 0,
      rotate: 0,
    },
    to: {
      scale: 1,
      width: 100,
      rotate: 360,
    },
    config: config.stiff,
    ref: growRef,
  })

  const { opacity } = useSpring({
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
    ref: opacityRef,
  })

  useChain(loaded ? [growRef, opacityRef] : [], [0.5, 1.5])

  return (
    <div name="projects" id="projects" className="projects">
      <div className="projects-head">
        <a.h1
          style={{ transform: scale.interpolate(scale => `scale(${scale})`) }}
        >
          Projects
        </a.h1>
        <a.hr
          className="projects-under"
          style={{ width: width.interpolate(width => `${width}%`) }}
        />
      </div>
      <a.p style={{ transform: scale.interpolate(scale => `scale(${scale})`) }}>
        These are some of my recent creations
      </a.p>
      <div className="filter-btns">
        <a.button
          style={{
            transform: interpolate(
              [scale, rotate],
              (scale, rotate) => `scale(${scale}) rotate(${rotate}deg)`
            ),
          }}
          className="all-filter"
          type="button"
          ref={el => (allFilter = el)}
        >
          All
        </a.button>
        <a.button
          style={{
            transform: interpolate(
              [scale, rotate],
              (scale, rotate) => `scale(${scale}) rotate(${rotate}deg)`
            ),
          }}
          className="js-filter"
          type="button"
          ref={el => (jsFilter = el)}
        >
          Pure JS
        </a.button>
        <a.button
          style={{
            transform: interpolate(
              [scale, rotate],
              (scale, rotate) => `scale(${scale}) rotate(${rotate}deg)`
            ),
          }}
          className="react-filter"
          type="button"
          ref={el => (reactFilter = el)}
        >
          React
        </a.button>
        <a.button
          style={{
            transform: interpolate(
              [scale, rotate],
              (scale, rotate) => `scale(${scale}) rotate(${rotate}deg)`
            ),
          }}
          className="node-filter"
          type="button"
          ref={el => (nodeFilter = el)}
        >
          Node
        </a.button>
      </div>
      <a.div
        style={{
          opacity,
        }}
        className="showcase"
        ref={el => (containerRef = el)}
      >
        {loaded && renderAll(images)}
      </a.div>
    </div>
  )
}

// Component to display a single project image and description
const Image = ({ imgSrc, data, desc }) => {
  // For flip animation
  const [flipped, setFlip] = useState(false)

  const { transform, opacity } = useSpring({
    opacity: flipped ? 1 : 0,
    transform: `perspective(600px) rotateX(${flipped ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 },
  })

  return (
    <div
      className="image"
      onClick={() => setFlip(!flipped)}
      data-groups={JSON.stringify(data)}
    >
      <a.div
        className="img-div"
        style={{
          opacity: opacity.interpolate(o => 1 - o),
          transform: transform.interpolate(t => `${t} translateY(-50%)`),
        }}
      >
        <Img fluid={imgSrc} />
      </a.div>
      <a.div
        className="img-info"
        style={{
          opacity,
          transform: transform.interpolate(t => `${t} rotateX(180deg)`),
        }}
      >
        {desc}
      </a.div>
    </div>
  )
}

export default Projects
